// Country gate brick — vanilla JS, no jQuery.
// One CountryGate instance per `[data-country-gate]` on the page.
// State lives only in window.history.state + in-memory toggles. No cookies, no storage.

class CountryCombobox {
    // ARIA Combobox with Listbox Popup (manual-select variant).
    // Visible trigger is a <button role="combobox">; the popup is a <ul role="listbox">
    // positioned beneath the trigger via CSS (top:100%) so direction is locked
    // downward regardless of viewport position. A hidden <select name="country">
    // is the canonical form value; the combobox writes into it on commit.

    constructor(root) {
        this.wrapper = root.querySelector('[data-gate-combobox]');
        if (!this.wrapper) {
            return;
        }

        this.native = this.wrapper.querySelector('[data-gate-combobox-native]');
        this.trigger = this.wrapper.querySelector('[data-gate-combobox-trigger]');
        this.list = this.wrapper.querySelector('[data-gate-combobox-list]');
        this.label = this.wrapper.querySelector('[data-gate-combobox-label]');

        if (!this.native || !this.trigger || !this.list || !this.label) {
            return;
        }

        this.options = Array.from(this.list.querySelectorAll('[role="option"]'));
        this.isOpen = false;
        this.activeIndex = -1;
        this.typeBuffer = '';
        this.typeBufferResetTimer = null;

        this.onDocumentMouseDown = this.onDocumentMouseDown.bind(this);

        this.bind();
    }

    bind() {
        this.trigger.addEventListener('click', this.onTriggerClick.bind(this));
        this.trigger.addEventListener('keydown', this.onTriggerKeyDown.bind(this));
        this.list.addEventListener('mousedown', this.onListMouseDown.bind(this));
        this.list.addEventListener('click', this.onListClick.bind(this));
    }

    onTriggerClick() {
        this.isOpen ? this.close() : this.open();
    }

    onTriggerKeyDown(e) {
        const key = e.key;

        if (!this.isOpen) {
            if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
                e.preventDefault();
                this.open();
                return;
            }
            if (key.length === 1 && /\S/.test(key)) {
                e.preventDefault();
                this.open();
                this.handleTypeAhead(key);
            }
            return;
        }

        switch (key) {
            case 'ArrowDown':
                e.preventDefault();
                this.moveActive(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.moveActive(-1);
                break;
            case 'Home':
                e.preventDefault();
                this.setActive(0);
                break;
            case 'End':
                e.preventDefault();
                this.setActive(this.options.length - 1);
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                this.commitActive();
                this.close();
                break;
            case 'Escape':
                e.preventDefault();
                this.close();
                break;
            case 'Tab':
                // Commit on Tab-out, then let focus move to the next field.
                this.commitActive();
                this.close();
                break;
            default:
                if (key.length === 1 && /\S/.test(key)) {
                    e.preventDefault();
                    this.handleTypeAhead(key);
                }
        }
    }

    onListMouseDown(e) {
        // Keep focus on the trigger so blur/close ordering stays predictable
        // when clicking an option.
        e.preventDefault();
    }

    onListClick(e) {
        const li = e.target.closest('[role="option"]');
        if (!li) return;
        const idx = this.options.indexOf(li);
        if (idx === -1) return;
        this.setActive(idx);
        this.commitActive();
        this.close();
        this.trigger.focus();
    }

    onDocumentMouseDown(e) {
        if (!this.wrapper.contains(e.target)) {
            this.close();
        }
    }

    open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.list.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');

        const currentValue = this.native.value;
        let idx = 0;
        if (currentValue) {
            const found = this.options.findIndex(o => o.dataset.value === currentValue);
            if (found >= 0) idx = found;
        }
        this.setActive(idx);

        document.addEventListener('mousedown', this.onDocumentMouseDown);
    }

    close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.list.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.setAttribute('aria-activedescendant', '');
        if (this.activeIndex >= 0 && this.options[this.activeIndex]) {
            this.options[this.activeIndex].classList.remove('is-active');
        }
        this.activeIndex = -1;

        document.removeEventListener('mousedown', this.onDocumentMouseDown);
        this.clearTypeBuffer();
    }

    moveActive(delta) {
        if (!this.options.length) return;
        const len = this.options.length;
        const next = ((this.activeIndex + delta) % len + len) % len;
        this.setActive(next);
    }

    setActive(idx) {
        if (idx < 0 || idx >= this.options.length) return;
        if (this.activeIndex >= 0 && this.options[this.activeIndex]) {
            this.options[this.activeIndex].classList.remove('is-active');
        }
        this.activeIndex = idx;
        const opt = this.options[idx];
        opt.classList.add('is-active');
        this.trigger.setAttribute('aria-activedescendant', opt.id);
        opt.scrollIntoView({ block: 'nearest' });
    }

    commitActive() {
        if (this.activeIndex < 0) return;
        const opt = this.options[this.activeIndex];
        const value = opt.dataset.value;
        const text = opt.textContent.trim();

        this.options.forEach((o, i) => {
            o.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false');
        });

        if (this.native.value !== value) {
            this.native.value = value;
            this.native.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.label.textContent = text;

        this.wrapper.dispatchEvent(new CustomEvent('country-change', {
            bubbles: true,
            detail: { value, text }
        }));
    }

    handleTypeAhead(char) {
        this.typeBuffer += char.toLowerCase();
        this.scheduleTypeBufferReset();

        const match = this.options.findIndex(o =>
            o.textContent.trim().toLowerCase().startsWith(this.typeBuffer)
        );
        if (match >= 0) {
            this.setActive(match);
        }
    }

    scheduleTypeBufferReset() {
        if (this.typeBufferResetTimer) clearTimeout(this.typeBufferResetTimer);
        this.typeBufferResetTimer = setTimeout(() => this.clearTypeBuffer(), 500);
    }

    clearTypeBuffer() {
        this.typeBuffer = '';
        if (this.typeBufferResetTimer) {
            clearTimeout(this.typeBufferResetTimer);
            this.typeBufferResetTimer = null;
        }
    }
}

class CountryGate {
    constructor(root) {
        this.root = root;

        let allowed = [];
        try {
            const dataNode = root.querySelector('[data-gate-data]');
            allowed = JSON.parse((dataNode && dataNode.textContent) || '[]');
        } catch (e) {
            allowed = [];
        }
        // Normalize to upper-case ISO-2 codes for safe comparison.
        this.allowed = allowed.map(c => String(c).toUpperCase());

        // Editor toggle: when off (default) an allowed country skips the
        // "Continue" confirmation view (2-allow) and lands on downloads (3).
        this.useContinueStep = root.dataset.useContinue === '1';

        this.views = {};
        root.querySelectorAll('[data-view]').forEach(el => {
            this.views[el.dataset.view] = el;
        });

        this.form = root.querySelector('[data-gate-form]');
        this.continueBtn = root.querySelector('[data-gate-continue]');

        this.combobox = new CountryCombobox(root);

        this.bind();
        this.installHistoryGuard();
        this.show('1');
    }

    bind() {
        if (this.form) {
            this.form.addEventListener('submit', this.onSubmit.bind(this));
            this.bindFloatingLabels();
        }
        if (this.continueBtn) {
            this.continueBtn.addEventListener('click', () => this.show('3'));
        }
    }

    // The shared `.form` floating-label JS (assets/js/modules/form.js) only
    // handles <input> and <textarea>. The country picker is now a custom
    // combobox (button trigger + listbox panel + hidden native <select>);
    // mirror the project's `.label-above` toggle on the trigger button, and
    // listen for the combobox's `country-change` event so the label moves
    // when a value is committed.
    bindFloatingLabels() {
        const triggers = this.form.querySelectorAll('.input-wrapper [data-gate-combobox-trigger]');
        triggers.forEach(trigger => {
            const wrapper = trigger.closest('.input-wrapper');
            if (!wrapper) return;
            const native = wrapper.querySelector('[data-gate-combobox-native]');

            const sync = () => {
                wrapper.classList.toggle('label-above', !!(native && native.value));
            };
            sync();

            trigger.addEventListener('focus', () => wrapper.classList.add('label-above'));
            trigger.addEventListener('blur', sync);
            wrapper.addEventListener('country-change', sync);
        });
    }

    show(viewId) {
        Object.entries(this.views).forEach(([id, el]) => {
            el.hidden = id !== viewId;
        });
        try {
            window.history.replaceState({ gateView: viewId }, '', window.location.pathname);
        } catch (e) {
            // older browsers without history API — fail silent
        }
    }

    onSubmit(e) {
        e.preventDefault();
        if (!this.form) return;

        const countryEl = this.form.elements.country;
        const cityEl = this.form.elements.city;

        const country = (countryEl && countryEl.value || '').toUpperCase().trim();
        const city = (cityEl && cityEl.value || '').trim();

        const countryWrapper = this.form.querySelector('[data-field="country"]');
        const cityWrapper = this.form.querySelector('[data-field="city"]');
        countryWrapper && countryWrapper.classList.toggle('is-error', !country);
        cityWrapper && cityWrapper.classList.toggle('is-error', !city);

        if (!country || !city) {
            return;
        }

        if (!this.allowed.includes(country)) {
            this.show('2-deny');
            return;
        }

        // Allowed country: respect editor toggle. Default = skip 2-allow.
        this.show(this.useContinueStep ? '2-allow' : '3');
    }

    installHistoryGuard() {
        // Defeat shallow back-button navigation: any popstate inside the gate
        // forces a fresh GET that reloads view 1.
        window.addEventListener('popstate', () => {
            window.location.replace(window.location.pathname);
        });

        // Safari occasionally restores the page from bfcache despite
        // Cache-Control: no-store. Force a reload when that happens so the
        // visitor never lands back on a granted/rejected view.
        window.addEventListener('pageshow', e => {
            if (e.persisted) {
                window.location.reload();
            }
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-country-gate]').forEach(root => {
        new CountryGate(root);
    });
});
